import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { TreeView, processTreeViewItems, handleTreeViewCheckChange } from '@progress/kendo-react-treeview';
import '@progress/kendo-theme-default/dist/all.css';
import { useTranslation } from 'react-i18next';

const KendoTreeView = forwardRef((props, ref) => {
    const { t }= useTranslation();
    const treeViewRef = useRef(null);
    const { data, selectedKeys, updateSelectedKeys, keysUpdated, className } = props;
    const [initialLoad, setInitialLoad] = React.useState(true);
    const [expand, setExpand] = React.useState({
        ids: [],
        idField: 'id'
    });
    const [treeData, setTreeData] = React.useState([{
        id: "-1", text: t("selectAll"), expanded: true, items: []
    }]);
    const treeDataItems = treeData[0].items;

    const handleItemSelection = itemKey => {
        const index = selectedKeys.indexOf(itemKey);
        const newSelectedKeys = [...selectedKeys];

        if (index === -1) {
            newSelectedKeys.push(itemKey);
        } else {
            newSelectedKeys.splice(index, 1);
        }
        // console.log('identify-treeview: newSelectedKeys', newSelectedKeys)
        updateSelectedKeys(newSelectedKeys);
    }

    const onExpandChange = event => {
        const ids = expand.ids ? expand.ids.slice() : [];
        const index = ids.indexOf(event.item.id);
        if (index === -1) {
            ids.push(event.item.id);
        } else {
            ids.splice(index, 1);
        }
        setExpand({
            ids,
            idField: 'id'
        });
    };

    const onItemClick = (event) => {
        onCheckChange(event);/**link selection and check change */
    };

    const onCheckChange = event => {
        const settings = {
            singleMode: false,
            checkChildren: true,
            checkParents: true
        };
        handleItemSelection(event.itemHierarchicalIndex);
        updateSelectedKeys(handleTreeViewCheckChange(event, selectedKeys, treeData, settings));
    };

    const updateTreeData = (newData) => {
        setTreeData(prevTreeData => [
            { ...prevTreeData[0], items: newData }
        ]);
    };

    React.useEffect(() => {
        let _treeData = [];
        let ids = ["-1"];
        data.filter(ilayer => ilayer.type !== 'graphics').forEach((layer) => {
            ids.push(layer.id);
            
            if (layer.layers?.items.length > 0 || layer.sublayers?.items.length > 0) {
                let childLayers = layer.layers ?? layer.sublayers;
                let treeDataItems = [];
                childLayers.items
                    .filter(sublayer => sublayer.visible)
                    .map(sublayer => {
                        ids.push(sublayer.id);
                        return {
                            id: sublayer.id,
                            text: sublayer.title,
                            url: sublayer.url,
                            layer: sublayer,
                        };
                    })
                    .forEach(item => treeDataItems.push(item));
                _treeData.push({
                    id: layer.id,
                    text: layer.title,
                    url: layer.url,
                    items: treeDataItems,
                });
            } else {
                _treeData.push({
                    id: layer.id,
                    text: layer.title,
                    url: layer.url,
                    layer: layer,
                });
            }
        });
        updateTreeData(_treeData);
        setExpand({
            ids,
            idField: 'id'
        });
    }, [data])

    const extractAndSaveIndices = (tdata) => {
        const fulltdata = treeViewRef.current.data;
        const indices = [];
    
        function traverse(node, path = '') {
            for (let i = 0; i < node.length; i++) {
                let currentPath = path ? `${path}_${i}` : `${i}`;
                indices.push(currentPath);
                if (node[i].items && node[i].items.length > 0) {
                    traverse(node[i].items, currentPath);
                }
            }
        }
        traverse(fulltdata, '');
        updateSelectedKeys(indices);
        return indices;
    }

    const selectAll = () => {
        extractAndSaveIndices(treeData);
    }

    React.useEffect(() => {
        if (initialLoad !== undefined && treeDataItems && treeDataItems.length > 0 && !keysUpdated) {
            if (initialLoad) {
                setInitialLoad(false);
                /** once tree data is ready keep track of item indices in selectedKeys */
                extractAndSaveIndices(treeData);
            }
        }
    }, [treeDataItems])

    useImperativeHandle(ref, () => ({
        getSelectedItems: (idata, ikeys) => {
            const data = treeViewRef.current.data[0].items;
            const keys = selectedKeys;
            const selectedItems = [];
            const selectedLayerItems = [];

            // console.log('selectedKeys', selectedKeys)
            const traverse = (items, parentIndex = '') => {
                items.forEach((item, index) => {
                    let layerItem = {
                        mapServiceId: item.id,
                        mapServiceUrl: item.url,
                        mapServiceName: item.text,
                        layersIds: [],
                        layersNames: [],
                    };
                    const currentIndex = parentIndex ? `0_${parentIndex}_${index}` : `0_${index}`;
                    let hasSelectedChildren = false;

                    if (item.items && item.items.length > 0) {
                        item.items.forEach((subItem, subIndex) => {
                            const childIndex = `${currentIndex}_${subIndex}`;
                            if (keys.includes(childIndex)) {
                                hasSelectedChildren = true;
                                layerItem.layersIds.push(subItem.id);
                                layerItem.layersNames.push(subItem.text);
                            }
                        });
                    }

                    if (keys.includes(currentIndex) || hasSelectedChildren) {
                        layerItem.mapServiceId = item.id;
                        layerItem.mapServiceUrl = item.url;
                        layerItem.mapServiceName = item.text;
                        selectedItems.push(item);

                        if (item.items) {
                            let sublayers = item.items;
                            for (var j = 0; j < sublayers.length; j++) {
                                if (sublayers[j].selected) {
                                    layerItem.layersIds.push(sublayers[j].id);
                                    layerItem.layersNames.push(sublayers[j].text);
                                }
                            }
                        }
                        selectedLayerItems.push(layerItem);
                    }
                });
            };

            traverse(data);
            return selectedLayerItems;
        },
        selectAll
    }));

    return (
        <div id='k-treeview' className={`${className ? className : ''} k-treeview-container`}>
            <TreeView
                data={processTreeViewItems(treeData, {
                    select: selectedKeys,
                    check: selectedKeys,
                    expand: expand
                })}
                expandIcons={true}
                onExpandChange={onExpandChange}
                aria-multiselectable={true}
                onItemClick={onItemClick}
                checkboxes={true}
                onCheckChange={onCheckChange}
                ref={treeViewRef}
            />
        </div>
    );
});
export default KendoTreeView;